/** @jsxImportSource @emotion/react */
import { ChevronRight } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  linkStyles,
  listItemRelative,
  navRoot,
  sectionList,
  sectionTitle,
  sidebarBackground,
  sidebarContainer,
  spaceY9,
  stickyNavigationWrapper,
} from './GuideSidebarNavigation.style'
import { appLink } from '@app/config/appLink'
import { GuideMainTopics } from '../types/guide.types'

export type GuideNavLink = { title: string; slug: string }
export type GuideNavSection = { title: string; links: GuideNavLink[] }

type GuideSidebarNavigationProps = {
  navigation?: GuideNavSection[]
  onLinkClick?: React.MouseEventHandler<HTMLAnchorElement>
  handleClickTopic?: () => void
  allTopicsActive?: boolean
  className?: string
}

export const GuideSidebarNavigation: React.FC<GuideSidebarNavigationProps> = ({
  navigation = [],
  onLinkClick,
  handleClickTopic,
  allTopicsActive,
  className,
}) => {
  const theme = useTheme()
  const { pathname } = useLocation()
  const isGlossary = pathname.includes(GuideMainTopics.GLOSSARY)
  return (
    <Box css={sidebarContainer} className={className}>
      <Box css={sidebarBackground({ theme })} />
      <Box css={stickyNavigationWrapper}>
        <Box component="nav" css={navRoot}>
          <Box component="ul" role="list" css={spaceY9}>
            <Box component="li">
              <Box
                component="h2"
                css={sectionTitle}
                role="button"
                tabIndex={0}
                aria-pressed={Boolean(allTopicsActive)}
                onClick={handleClickTopic}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleClickTopic?.()
                  }
                }}
              >
                {allTopicsActive ? 'View Current Topic' : 'Browse All Topics'}
                <ChevronRight />
              </Box>
            </Box>
            {navigation.map((section) => (
              <Box component="li" key={section.title}>
                <Box component="h2" css={sectionTitle}>
                  {section.title}
                  <ChevronRight />
                </Box>
                <Box component="ul" role="list" css={sectionList}>
                  {section.links.map((link) => (
                    <Box component="li" key={link.slug} css={listItemRelative}>
                      <NavLink
                        style={{ textDecoration: 'none' }}
                        to={link.slug}
                        end={false}
                        onClick={onLinkClick}
                      >
                        {({ isActive }) => (
                          <span css={linkStyles(isActive, theme)}>{link.title}</span>
                        )}
                      </NavLink>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

            {allTopicsActive ||
              (isGlossary && (
                <Box component="li">
                  <Box component="h2" css={sectionTitle}>
                    Glossary
                    <ChevronRight />
                  </Box>
                  <Box component="ul" role="list" css={sectionList}>
                    <Box component="li" css={listItemRelative}>
                      <NavLink
                        style={{ textDecoration: 'none' }}
                        to={`${appLink.guidePage.path + '/' + GuideMainTopics.GLOSSARY}`}
                        end={false}
                        onClick={onLinkClick}
                      >
                        {({ isActive }) => (
                          <span css={linkStyles(isActive, theme)}>{GuideMainTopics.GLOSSARY}</span>
                        )}
                      </NavLink>
                    </Box>
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
